import { useState, useEffect, useCallback } from 'react'

/**
 * Hook to detect and monitor the user's internet connectivity status.
 * Uses the Navigator.onLine API combined with online/offline events.
 * Also performs an optional fetch check for more accurate detection.
 */
export function useOnlineStatus(): {
  isOnline: boolean
  isChecking: boolean
  lastChecked: Date | null
  checkConnection: () => Promise<boolean>
} {
  const [isOnline, setIsOnline] = useState<boolean>(true)
  const [isChecking, setIsChecking] = useState(false)
  const [lastChecked, setLastChecked] = useState<Date | null>(null)

  // Perform a real network check by attempting to fetch a small resource
  const checkConnection = useCallback(async (): Promise<boolean> => {
    setIsChecking(true)
    try {
      // Try to fetch a small, fast endpoint
      // We use a HEAD request to minimize data transfer
      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), 5000)
      
      const response = await fetch('/favicon.ico', {
        method: 'HEAD',
        cache: 'no-store',
        signal: controller.signal
      })
      
      clearTimeout(timeoutId)
      const online = response.ok
      setIsOnline(online)
      setLastChecked(new Date())
      return online
    } catch {
      // If fetch fails, we're likely offline
      setIsOnline(false)
      setLastChecked(new Date())
      return false
    } finally {
      setIsChecking(false)
    }
  }, [])

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true)
      setLastChecked(new Date())
    }

    const handleOffline = async () => {
      // navigator.onLine can give false negatives — verify with a real fetch
      try {
        const controller = new AbortController()
        const tid = setTimeout(() => controller.abort(), 3000)
        const resp = await fetch('/favicon.ico', { method: 'HEAD', cache: 'no-store', signal: controller.signal })
        clearTimeout(tid)
        if (resp.ok) {
          setIsOnline(true)
          return
        }
      } catch { /* genuinely offline */ }
      setIsOnline(false)
      setLastChecked(new Date())
    }

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    // Initial check: if navigator says offline, verify with a real fetch
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      handleOffline()
    }

    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [])

  return {
    isOnline,
    isChecking,
    lastChecked,
    checkConnection
  }
}

export default useOnlineStatus
